import { Spin, message } from "antd";
import { useEffect, useState } from "react";
import EhrClinicianList, { type EhrClinicianRow } from "../../components/EhrClinicianList/EhrClinicianList.component";
import listStyles from "../../components/EhrClinicianList/EhrClinicianList.module.scss";
import { useAppSelector } from "../../hooks/redux-hooks";
import {
  useGetEhrCliniciansQuery,
  useInviteEhrClinicianMutation,
  useRefreshEhrCliniciansMutation,
} from "../../service/groupOnboarding.service";

/** Re-read cadence while a refresh (a real EHR login, tens of seconds) is running. */
const POLL_MS = 4000;

type ApiError = { data?: { detail?: string; email?: string[] } };

/** Member Management's host for the shared EHR clinician list (logged-in transport). */
export default function EhrClinicianInvite({ onInvited }: { onInvited?: () => void }) {
  const isRpaGroup = Boolean(useAppSelector((state) => state.auth.userInfo.group_rpa_enabled));
  const [pollingInterval, setPollingInterval] = useState(0);
  const { data, isLoading } = useGetEhrCliniciansQuery(undefined, { skip: !isRpaGroup, pollingInterval });
  const [refreshClinicians, { isLoading: isRefreshing }] = useRefreshEhrCliniciansMutation();
  const [inviteClinician] = useInviteEhrClinicianMutation();
  const [refreshError, setRefreshError] = useState("");

  // Poll only while the server reports a pull in flight.
  useEffect(() => {
    setPollingInterval(data?.is_pulling ? POLL_MS : 0);
  }, [data]);

  const refresh = async () => {
    setRefreshError("");
    try {
      await refreshClinicians().unwrap();
    } catch (err) {
      setRefreshError((err as ApiError)?.data?.detail || "We couldn't refresh the list just now. Please try again.");
    }
  };

  const invite = async (email: string, row: EhrClinicianRow) => {
    try {
      const res = await inviteClinician({ email, name: row.name, ehr_provider_id: row.ehr_provider_id }).unwrap();
      message.success(res.state === "joined" ? `${email} was added to your team.` : `Invitation sent to ${email}.`);
      onInvited?.();
      return res.state;
    } catch (err) {
      const d = (err as ApiError)?.data;
      throw new Error(d?.email?.[0] || d?.detail || "We couldn't send that invitation. Please try again.");
    }
  };

  if (!isRpaGroup) return null;
  if (isLoading) {
    return (
      <div style={{ padding: "8px 0 16px" }}>
        <Spin size="small" />
      </div>
    );
  }
  const providers = data?.has_directory ? data.providers : [];
  if (providers.length === 0) return null;

  return (
    <div className={listStyles.host}>
      <EhrClinicianList
        providers={providers}
        onInvite={invite}
        onRefresh={refresh}
        isRefreshing={isRefreshing || Boolean(data?.is_pulling)}
        refreshError={refreshError}
      />
    </div>
  );
}
